(() => {
  const namespace = 'BJ4HomeCommerce';

  if (window[namespace]) {
    window[namespace].init(document);
    return;
  }

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const initRails = (root) => {
    root.querySelectorAll('[data-bj4-rail]').forEach((rail) => {
      if (rail.dataset.bj4Ready === 'true') return;
      rail.dataset.bj4Ready = 'true';

      const viewport = rail.querySelector('[data-bj4-rail-viewport]');
      const previous = rail.querySelector('[data-bj4-rail-prev]');
      const next = rail.querySelector('[data-bj4-rail-next]');
      const progress = rail.querySelector('[data-bj4-rail-progress]');

      if (!viewport || !previous || !next) return;

      const update = () => {
        const maximum = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
        const position = Math.min(maximum, Math.max(0, viewport.scrollLeft));
        previous.disabled = position <= 2;
        next.disabled = maximum <= 2 || position >= maximum - 2;
        if (progress) {
          progress.style.setProperty('--bj4-rail-progress', maximum > 0 ? String(position / maximum) : '1');
        }
      };

      const move = (direction) => {
        viewport.scrollBy({
          left: direction * Math.max(260, viewport.clientWidth * 0.82),
          behavior: reducedMotion.matches ? 'auto' : 'smooth'
        });
      };

      previous.addEventListener('click', () => move(-1));
      next.addEventListener('click', () => move(1));
      viewport.addEventListener('scroll', () => window.requestAnimationFrame(update), { passive: true });
      viewport.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        move(event.key === 'ArrowLeft' ? -1 : 1);
      });

      if ('ResizeObserver' in window) {
        const resizeObserver = new ResizeObserver(update);
        resizeObserver.observe(viewport);
      } else {
        window.addEventListener('resize', update, { passive: true });
      }

      update();
    });
  };

  const formatRemaining = (milliseconds) => {
    const totalMinutes = Math.max(1, Math.ceil(milliseconds / 60000));
    const days = Math.floor(totalMinutes / 1440);
    const hours = Math.floor(totalMinutes / 60) % 24;
    const minutes = totalMinutes % 60;
    return { en: (days ? days + 'd ' : '') + (days || hours ? hours + 'h ' : '') + minutes + 'm', ja: '' };
  };

  const initCountdowns = (root) => {
    root.querySelectorAll('[data-bj4-section="new-rail"], [data-bj4-section="new-arrivals-page"]').forEach((section) => {
      if (section.dataset.bj4CountdownReady === 'true') return;
      section.dataset.bj4CountdownReady = 'true';

      const countdowns = [...section.querySelectorAll('[data-bj4-countdown]')];
      const toggle = section.querySelector('[data-bj4-countdown-toggle]');
      const toggleLabel = toggle?.querySelector('[data-bj4-countdown-toggle-label]');
      const toggleLabelJapanese = toggle?.querySelector('[data-bj4-countdown-toggle-ja]');
      let paused = false;

      const pulseLedgerOnce = () => {
        if (reducedMotion.matches || section.dataset.bj4LedgerPulsed === 'true') return;
        section.dataset.bj4LedgerPulsed = 'true';
        section.classList.add('is-ledger-pulsing');
        window.setTimeout(() => section.classList.remove('is-ledger-pulsing'), 4000);
      };

      if (countdowns.length && 'IntersectionObserver' in window) {
        const pulseObserver = new IntersectionObserver((entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          pulseLedgerOnce();
          pulseObserver.disconnect();
        }, { threshold: 0.18 });
        pulseObserver.observe(section);
      } else if (countdowns.length) {
        pulseLedgerOnce();
      }

      countdowns.forEach((countdown) => {
        if (!countdown.dataset.endTimestamp && countdown.dataset.demoDays) {
          const demoDays = Number.parseInt(countdown.dataset.demoDays, 10);
          if (Number.isFinite(demoDays)) {
            countdown.dataset.endTimestamp = String(Math.floor(Date.now() / 1000) + demoDays * 86400);
          }
        }
      });

      const update = () => {
        if (document.hidden) return;

        countdowns.forEach((countdown) => {
          const endTimestamp = Number.parseInt(countdown.dataset.endTimestamp || '', 10);
          const value = countdown.querySelector('[data-bj4-countdown-value]');
          const japaneseValue = countdown.querySelector('[data-bj4-countdown-ja]');
          const card = countdown.closest('[data-bj4-offer-card]');

          if (!Number.isFinite(endTimestamp) || !value) return;

          const remaining = endTimestamp * 1000 - Date.now();
          if (remaining <= 0) {
            card?.classList.add('is-offer-expired');
            countdown.hidden = true;
            card?.querySelectorAll('[data-new-offer-price]').forEach((price) => { price.textContent = price.dataset.regularPrice; });
            card?.querySelectorAll('.bj4-new-card__new, .bj4-new-page-card__badge').forEach((badge) => { badge.hidden = true; });
            return;
          }

          if (paused) return;
          card?.classList.remove('is-offer-expired');
          const last48Hours = remaining <= 48 * 60 * 60 * 1000;
          const lastWeek = !last48Hours && remaining <= 7 * 24 * 60 * 60 * 1000;
          countdown.classList.toggle('is-last-48h', last48Hours);
          countdown.classList.toggle('is-last-week', lastWeek);
          countdown.dataset.offerStage = last48Hours ? 'last-48h' : lastWeek ? 'last-week' : 'standard';
          const formatted = formatRemaining(remaining);
          value.textContent = formatted.en;
          if (japaneseValue) japaneseValue.textContent = `終了まで ${formatted.ja}`;
        });
      };

      if (toggle) {
        toggle.addEventListener('click', () => {
          paused = !paused;
          toggle.setAttribute('aria-pressed', String(paused));
          if (toggleLabel) toggleLabel.textContent = paused ? 'Resume offer timers' : 'Pause offer timers';
          if (toggleLabelJapanese) toggleLabelJapanese.textContent = paused ? '終了表示を再開' : '終了表示を一時停止';
          if (!paused) update();
        });
      }

      update();
      if (countdowns.length) {
        const interval = window.setInterval(() => {
          if (!section.isConnected) {
            window.clearInterval(interval);
            return;
          }
          update();
        }, 1000);
      }
    });
  };

  const init = (root = document) => {
    initRails(root);
    initCountdowns(root);
  };

  window[namespace] = { init };
  init(document);

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
